import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { RBACService } from '../../../shared/rbac.service';
import { UtilsService } from '../../../shared/utils.service';
import { CustomDataSource } from '../../../utils/custom-data-source';
import { CourseModuleModel } from 'src/app/models/course-module.model';
import { CoursesModulesEditComponent } from '../courses-modules-edit/courses-modules-edit.component';
import { CoursesModulesViewComponent } from '../courses-modules-view/courses-modules-view.component';

@Component({
  selector: 'app-courses-modules',
  templateUrl: './courses-modules.component.html',
  styleUrls: ['./courses-modules.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesModulesComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];
  dataSource = new ModulesDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private dialog: MatDialog,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access account inductions.'
      );
      this.router.navigate(['/courses']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = ['id', 'name', 'date_created', 'actions'];
    if (this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // get the course id
    this.dataSource.parent_id = Number(this.route.snapshot.params['course_id']);

    if (this.dataSource.parent_id) {
      // 4. Only load data if user has view permission.
      if (this.canView) {
        this.dataSource.getData();
      }
    } else {
      // redirect back to courses because the course id is not valid
      this.router.navigate(['courses']);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC Columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create induction modules.'
      );
      return;
    }
    this.openEditDialog(new CourseModuleModel());
  }

  onView(courseModule: CourseModuleModel) {
    this.dialog
      .open(CoursesModulesViewComponent, {
        width: '900px',
        data: courseModule
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onEdit(courseModule: CourseModuleModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit induction modules.'
      );
      return;
    }
    this.openEditDialog(courseModule);
  }

  private openEditDialog(courseModule: CourseModuleModel) {
    this.dialog
      .open(CoursesModulesEditComponent, {
        width: '900px',
        data: {
          module: { ...courseModule },
          parent_id: this.dataSource.parent_id
        }
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction modules.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Module',
      'Are you sure you want to remove this induction module?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${this.dataSource.parent_id}/modules/${id}`
          )
          .then((response: any) => {
            this.utils.showToast('The induction module is deleted.');
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction modules.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Module',
      'Are you sure you want to remove the selected induction modules?',
      () => {
        const selectedModules = this.dataSource.selection.selected.join(',');

        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${
              this.dataSource.parent_id
            }/modules/${selectedModules}`
          )
          .then((response: any) => {
            this.utils.showToast('The selected induction modules is deleted.');
            this.dataSource.selection.clear();
            this.dataSource.getData();
          });
      }
    );
  }
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class ModulesDataSource extends CustomDataSource {
  parent_id = 0;

  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      `inductions/${this.parent_id}/modules`,
      resetOffset
    );
  }
}
